<template>
    <div>
        <section style="min-height: 55vh">
            <div class="container">

                <currency-select class="q-mb-md" />
                <div v-show="!verifyOrderMode" class="text-title">Shopping Cart</div>
                <div v-show="verifyOrderMode" class="row q-mb-md">
                    <div class="text-title col-xs-12 col-md-8">Verify Order</div>
                    <div class="col-xs-12 col-md-8">Your order is ready for processing. Please review the following details and edit if necessary. <br /> Click the checkout button to proceed to the payment page.</div>
                </div>

                <q-card-section class="full-width q-ma-none q-pa-none">

                    <!-- when cart has items -->
                    <div v-show="cart.cartItems.length > 0" class="row q-col-gutter-x-md">

                        <!-- cart item listing -->
                        <div class="col-xs-12 col-md-8">

                            <transition name="slide-fade">
                                <q-banner inline-actions v-show="showAskInstallID"
                                          class="bg-secondary text-white box shadow-2 q-mb-md install-id-top-banner">
                                    <template v-slot:avatar>
                                        <q-icon name="mdi-information" size="sm" />
                                    </template>
                                    <template v-slot:default>
                                        <span class="text-bold" style="font-size: 1rem; display: block;">Install ID</span>
                                        <span class="text-caption">
                                            If you enter the Install ID from your software, then your Activation Code can be automatically generated for you.
                                            If you are purchasing multiple copies, then proceed without entering the Install ID.
                                        </span>
                                    </template>
                                    <template v-slot:action>
                                        <q-btn unelevated color="info" label="Dismiss" size="sm" @click="$store.commit('update_installIdDismissed', true)" />
                                    </template>
                                </q-banner>
                            </transition>

                            <q-card v-show="!verifyOrderMode" class="row items-center q-mb-md q-pa-md"
                                    v-bind:class="$q.dark.isActive ? 'bg-dark-filled' : 'bg-white'"
                                    v-for="(item, index) in cart.cartItems" :key="item.id">

                                <div class="col-xs-12 col-sm-7 q-pb-md q-pr-sm">

                                    <div class="text-subtitle2">{{ item.description }}</div>

                                    <div v-if="item.customDescription"
                                         v-html="item.customDescription"
                                         class="text-caption q-my-xs" />

                                    <div class="row q-col-gutter-xs q-mt-xs">

                                        <q-input dense outlined
                                                 class="col-12"
                                                 v-if="!item.product.isSubscription && !item.product.isUpgrade"
                                                 v-model.trim="item.installationID"
                                                 label="Install ID" stack-label
                                                 autocomplete="on" name="installationId"
                                                 :error-message="item.isValidIdErrorMessage"
                                                 :error="typeof item.isValidId !== 'undefined' && !item.isValidId"
                                                 :disable="item.quantity > 1"
                                                 @change="checkValidItems(item)"
                                                 hide-bottom-space>
                                            <template v-slot:append>
                                                <transition name="fade">
                                                    <q-icon name="mdi-information" v-show="!showAskInstallID" class="cursor-pointer" style="color: rgba(0, 0, 0, 0.24);">
                                                        <q-popup-proxy :content-class="popupPosition.contentClass"
                                                                       :anchor="popupPosition.anchor"
                                                                       :self="popupPosition.self"
                                                                       transition-show="scale"
                                                                       transition-hide="scale">
                                                            <q-banner class="bg-secondary text-white box shadow-2" :class="popupPosition.arrow" style="max-width: 425px">
                                                                <span class="text-caption">
                                                                    If you enter the Install ID from your software, then your Activation Code can be automatically generated for you.
                                                                    If you are purchasing multiple copies, then proceed without entering the Install ID.
                                                                </span>
                                                            </q-banner>
                                                        </q-popup-proxy>
                                                    </q-icon>
                                                </transition>
                                            </template>
                                        </q-input>

                                        <q-input dense outlined
                                                 class="col-12"
                                                 label="Discount Code" stack-label
                                                 autocomplete="off"
                                                 v-model.trim="item.discountCode"
                                                 :error-message="item.isValidDiscountCodeErrorMessage"
                                                 :error="typeof item.isValidDiscountCode !== 'undefined' && !item.isValidDiscountCode"
                                                 :hint="item.discountDescription || undefined"
                                                 @change="onDiscountCodeChange(item)"
                                                 hide-bottom-space />

                                    </div>
                                </div>

                                <div class="col-xs-8 col-sm-3 text-center">

                                    <div class="row items-center" style="margin: 30px 14px 16px;">

                                        <div class="col" style="min-width: 60px; max-width: 60px;">

                                            <q-input v-model.number="item.quantity"
                                                     dense outlined
                                                     label="Qty" stack-label
                                                     @change="updateQuantity(item)"
                                                     :disable="item.product.isSubscription || item.product.isUpgrade"
                                                     input-class="text-center"
                                                     type="number"
                                                     :rules="[ val => val > 0 || '' ]"
                                                     hide-bottom-space />

                                        </div>

                                        <div class="col-auto">

                                            <q-btn flat icon="mdi-delete-outline" size="md" color="negative" padding="xs" style="margin-left: 4px;"
                                                   @click="deleteItem(item)" />

                                        </div>
                                    </div>
                                </div>

                                <div class="col-xs-4 col-sm-2 text-right text-subtitle2" style="margin: 30px 0 16px 0;">

                                    <div>
                                        {{ item.grossTotal | currency }}
                                    </div>

                                    <div v-if="item.discountTotal"
                                         class="text-negative">
                                        -{{ item.discountTotal | currency }}
                                    </div>

                                    <div v-if="item.multiDiscountTotal"
                                         class="text-caption text-negative">

                                        <div class="cursor-pointer">
                                            (Saved: {{ item.multiDiscountTotal | currency }})

                                            <q-popup-proxy>

                                                <q-card style="max-width: 400px;" class="full-width">

                                                    <q-card-section>

                                                        <div class="text-subtitle1 row items-center">
                                                            <q-icon name="mdi-sale" size="sm" class="q-mr-xs" />Multiple License Discount
                                                            <q-space />
                                                            <q-btn icon="mdi-close"
                                                                   flat round v-close-popup />
                                                        </div>

                                                    </q-card-section>

                                                    <q-card-section class="q-pt-none">

                                                        <q-markup-table separator="cell" dense>
                                                            <thead class="bg-secondary text-white">
                                                                <tr>
                                                                    <th class="text-left">Quantity</th>
                                                                    <th class="text-center">Discount</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                <tr>
                                                                    <td class="text-left">First License</td>
                                                                    <td class="text-center">0%</td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="text-left">License 2-5</td>
                                                                    <td class="text-center">10%</td>
                                                                </tr>
                                                                <tr>
                                                                    <td class="text-left">License >5</td>
                                                                    <td class="text-center">15%</td>
                                                                </tr>
                                                            </tbody>
                                                        </q-markup-table>
                                                    </q-card-section>

                                                    <q-separator inset />

                                                    <q-card-section class="text-caption">

                                                        <div class="text-subtitle2">Important Terms and Conditions</div>

                                                        <ol>
                                                            <li>Discounts do not apply to upgrades or subscription renewals. They only apply to new Full licenses.</li>
                                                            <li>Discounts cannot be used with any other discounts.</li>
                                                            <li>Restricted to licenses purchased by the same company, at the same location.</li>
                                                            <li>The discount rates shown apply to the copies in the specified quantity range only, not to the total quantity.</li>
                                                        </ol>

                                                    </q-card-section>

                                                </q-card>

                                            </q-popup-proxy>

                                        </div>
                                    </div>
                                </div>

                                <div v-show="typeof item.isValidProductForLicense !== 'undefined' && !item.isValidProductForLicense" class="text-negative text-caption q-mt-xs" v-html="item.isValidProductForLicenseErrorMessage">
                                </div>

                            </q-card>

                            <q-card v-show="verifyOrderMode" class="row items-center q-mb-md q-pa-md"
                                    v-bind:class="$q.dark.isActive ? 'bg-dark-filled' : 'bg-white'"
                                    v-for="(item, index) in cart.cartItems" :key="item.id">

                                <div class="col-xs-12 col-sm-7 q-pr-sm">

                                    <div class="text-subtitle2">{{ item.description }}</div>

                                    <div v-if="item.customDescription"
                                         v-html="item.customDescription"
                                         class="text-caption q-my-xs" />

                                    <div v-if="item.discountDescription" class="row q-col-gutter-xs q-mt-xs">

                                        <div class="text-caption">Discount: {{ item.discountDescription }}</div>

                                    </div>
                                </div>

                                <div class="col-xs-8 col-sm-3 text-center">

                                    <div class="row items-center q-ma-md">

                                        <div class="col">
                                            <div><span class="text-caption q-pr-sm" style="font-size: 0.8em; vertical-align: top;">Qty</span>{{ item.quantity }}</div>
                                        </div>

                                    </div>
                                </div>

                                <div class="col-xs-4 col-sm-2 text-right text-subtitle2">

                                    <div>
                                        {{ item.grossTotal | currency }}
                                    </div>

                                    <div v-if="item.discountTotal"
                                         class="text-negative">
                                        -{{ item.discountTotal | currency }}
                                    </div>

                                    <div v-if="item.multiDiscountTotal"
                                         class="text-caption text-negative">

                                        <div>
                                            (Saved: {{ item.multiDiscountTotal | currency }})
                                        </div>
                                    </div>
                                </div>
                            </q-card>

                        </div>

                        <!-- cart summary -->
                        <div class="col-xs-12 col-md-4 q-pb-md">

                            <q-card class="q-pa-md full-height" v-bind:class="$q.dark.isActive ? 'bg-dark-filled' : 'bg-white'">

                                <div class="row col">
                                    <div class="text-subtitle1 q-mb-sm">Cart Summary</div>
                                </div>

                                <div class="row">

                                    <div class="col">
                                        Sub Total
                                    </div>

                                    <div class="col text-right">
                                        {{ cart.subTotal | currency }}
                                    </div>

                                </div>

                                <div class="row">

                                    <div class="col">
                                        {{ cart.taxLabel }}
                                    </div>

                                    <div class="col text-right">
                                        {{ cart.taxes | currency }}
                                    </div>

                                </div>

                                <div class="row text-bold">

                                    <div class="col">
                                        Total
                                    </div>

                                    <div class="col text-right">
                                        {{ cart.total | currency }}
                                    </div>

                                </div>

                            </q-card>

                        </div>

                    </div>

                    <!-- when cart has no items -->
                    <q-card v-show="cart.cartItems.length <= 0" style="min-height: 300px" class="row justify-center items-center bg-grey-2">

                        <div class="text-center">

                            <q-icon name="mdi-cart-outline" size="4.3em" class="q-ma-sm" />

                            <div class="text-h5">Your cart is empty</div>

                            <q-btn color="primary" label="Let's go shopping" class="q-ma-sm" :class="{ 'full-width' : screenSize.xs }" to="/store" />

                        </div>

                    </q-card>

                    <!-- when cart has items; buttons and footnotes -->
                    <div v-show="cart.cartItems.length > 0" class="text-right">

                        <!-- buttons -->
                        <div class="row justify-end q-col-gutter-sm">

                            <div class="col-xs-12 col-sm-auto">

                                <q-btn v-show="verifyOrderMode" label="Edit Cart" icon="mdi-basket" color="primary" @click="verifyOrderMode = false" :disabled="getPaymentSessionIdPending" class="full-width" />
                                <q-btn v-show="!verifyOrderMode" label="Continue Shopping" icon="mdi-basket" color="primary" to="/store" :disabled="getPaymentSessionIdPending" class="full-width" />

                            </div>

                            <div class="col-xs-12 col-sm-auto">

                                <q-btn v-show="verifyOrderMode" label="Checkout" :icon="isLoggedIn ? 'mdi-cart-arrow-right' : 'mdi-login'" color="positive" :disabled="getPaymentSessionIdPending || isStoreDisabledPending || isStoreDisabledData || !isCartValidForPurchase" @click="checkout" :loading="getPaymentSessionIdPending || loading" class="full-width" />
                                <q-btn v-show="!verifyOrderMode" label="Continue" icon="mdi-cart-arrow-right" color="positive" :disabled="getPaymentSessionIdPending || isStoreDisabledPending || isStoreDisabledData || !isCartValidForPurchase" @click="continueToVerifyOrder" :loading="getPaymentSessionIdPending || loading" class="full-width" />

                            </div>

                        </div>

                        <!-- footnotes -->
                        <div class="text-caption text-grey text-right q-mt-sm">

                            <span>Your credit card will not be charged yet.</span>

                            <div class="row justify-end items-center">

                                Powered by <!-- stripe svg logo -->
                                <svg class="icon q-mx-xs" focusable="false" width="33" height="15" fill="grey">
                                    <g fill-rule="evenodd">
                                        <path d="M32.956 7.925c0-2.313-1.12-4.138-3.261-4.138-2.15 0-3.451 1.825-3.451 4.12 0 2.719 1.535 4.092 3.74 4.092 1.075 0 1.888-.244 2.502-.587V9.605c-.614.307-1.319.497-2.213.497-.876 0-1.653-.307-1.753-1.373h4.418c0-.118.018-.588.018-.804zm-4.463-.859c0-1.02.624-1.445 1.193-1.445.55 0 1.138.424 1.138 1.445h-2.33zM22.756 3.787c-.885 0-1.454.415-1.77.704l-.118-.56H18.88v10.535l2.259-.48.009-2.556c.325.235.804.57 1.6.57 1.616 0 3.089-1.302 3.089-4.166-.01-2.62-1.5-4.047-3.08-4.047zm-.542 6.225c-.533 0-.85-.19-1.066-.425l-.009-3.352c.235-.262.56-.443 1.075-.443.822 0 1.391.922 1.391 2.105 0 1.211-.56 2.115-1.39 2.115zM18.04 2.766V.932l-2.268.479v1.843zM15.772 3.94h2.268v7.905h-2.268zM13.342 4.609l-.144-.669h-1.952v7.906h2.259V6.488c.533-.696 1.436-.57 1.716-.47V3.94c-.289-.108-1.346-.307-1.879.669zM8.825 1.98l-2.205.47-.009 7.236c0 1.337 1.003 2.322 2.34 2.322.741 0 1.283-.135 1.581-.298V9.876c-.289.117-1.716.533-1.716-.804V5.865h1.716V3.94H8.816l.009-1.96zM2.718 6.235c0-.352.289-.488.767-.488.687 0 1.554.208 2.241.578V4.202a5.958 5.958 0 0 0-2.24-.415c-1.835 0-3.054.957-3.054 2.557 0 2.493 3.433 2.096 3.433 3.17 0 .416-.361.552-.867.552-.75 0-1.708-.307-2.467-.723v2.15c.84.362 1.69.515 2.467.515 1.879 0 3.17-.93 3.17-2.548-.008-2.692-3.45-2.213-3.45-3.225z"></path>
                                    </g>
                                </svg>

                            </div>
                        </div>

                    </div>

                </q-card-section>

                <q-inner-loading :showing="syncCartPending">
                    <div>
                        <q-spinner color="primary"
                                   :thickness="2"
                                   size="3em" />
                    </div>
                </q-inner-loading>

                <q-inner-loading :showing="getPaymentSessionIdPending || loading">
                    <div></div>
                </q-inner-loading>

                <q-inner-loading :showing="cartLockedForProcessing">
                    <div style="position: absolute; top: 25vh; text-align: center; box-shadow: 0 1px 5px rgba(0, 0, 0, 0.2), 0 2px 2px rgba(0, 0, 0, 0.14), 0 3px 1px -2px rgba(0, 0, 0, 0.12);" class="bg-white rounded-borders q-pa-xl">
                        <label class="text-h6 text-bold">Cart Locked for Order Processing</label>
                        <div class="row justify-end q-col-gutter-sm q-mt-md">
                            <div class="col-xs-12 col-sm-auto">
                                <q-btn label="Unlock Cart To Edit Order" icon="mdi-lock-open" color="positive" @click="unlockCart" class="full-width" />
                            </div>
                            <div class="col-xs-12 col-sm-auto">
                                <q-btn label="Clear Cart" icon="mdi-delete" color="positive" @click="$store.commit('reset_cart')" class="full-width" />
                            </div>
                        </div>
                    </div>
                </q-inner-loading>

            </div>
        </section>

        <section class="bg-grey-2">
            <div class="container">

                <p class="text-subtitle1 text-weight-medium">Terms & Conditions of Sale</p>
                <ol class="text-caption">
                    <li>Sales of software are final once an activation code has been issued. Free trials are available for testing and evaluation prior to purchasing.</li>

                    <li>The currency for the transaction is either US dollars or Canadian dollars as shown above. For credit cards issued in other currencies, your credit card company determines the exchange rate used for the transaction.</li>

                    <li>Refer to the End User License Agreement for full terms and conditions.</li>
                </ol>
            </div>
        </section>

        <section>
            <div class="container">

                <p class="text-subtitle1 text-weight-medium">Notice to US and International Customers</p>
                <div class="text-caption">Please be advised that your bank may apply an additional fee for an international transaction, typically in the range of 2% - 4%. Although Avenir's Merchant Account used for these transactions is a US$ Merchant Account your bank may still choose to apply this additional fee. Avenir has no control over any such fees and we will not be able to reverse or refund them. To dispute these fees if applied to you, you would need to contact your bank directly.</div>
            </div>
        </section>

        <stripe-checkout ref="checkoutRef"
                         :pk="publishableKey"
                         :session-id="checkoutSessionId"
                         @loading="v => loading = v"
                         @error="stripeErrorHandler" />

        <q-dialog v-model="showLogin">
            <login @close="closeLoginDialog" style="width: 100%; max-width: 375px;" />
        </q-dialog>

        <dialog-license-oem-locked :show.sync="showLicenseOemLockedDialogOnContinue"
                                   :items="oemLockedLicenseItems" />

    </div>
</template>

<script>
    import { StripeCheckout } from '@vue-stripe/vue-stripe'
    import { mapState } from 'vuex'
    import { Notify } from 'quasar'
    import dialogLicenseOemLocked from '@/components/dialogs/DialogLicenseOemLocked'
    import currencySelect from '@/components/CurrencySelect'
    import login from '@/components/Login'

    export default {
        name: 'cart',
        components: {
            login,
            StripeCheckout,
            currencySelect,
            dialogLicenseOemLocked,
        },
        data() {
            this.publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY
            return {
                showLogin: false,
                loading: false,
                licensesForRenewUpgrade: null,
                reinstatementLicenseItems: null,
                verifyOrderMode: false,
                showLicenseOemLockedDialogOnContinue: false,
            }
        },
        methods: {
            stripeErrorHandler(err) {
                this.loading = false
                this.$q.notify({
                    badgeClass: 'hidden',
                    type: 'negative',
                    message: err.message
                })
            },
            isStoreDisabled() {
                this.$store.dispatch('isStoreDisabled').then(() => {
                    if (this.isStoreDisabledData) {
                        Notify.create({
                            badgeClass: 'hidden',
                            type: 'negative',
                            position: 'bottom',
                            message: 'Purchasing is Currently Unavailable, Please Try Again Later.'
                        });
                        return true;
                    }
                    else {
                        return false;
                    }
                })
            },
            continueToVerifyOrder() {
                try {
                    if (!this.isLoggedIn) {
                        this.$store.commit('update_continueToVerifyOrderClicked', true)
                        this.showLogin = true
                        return
                    }

                    this.showLicenseOemLockedDialogOnContinue = this.showLicenseOemLockedDialog;

                    this.$store.dispatch('isCartOrderEditable', this.cart.orderID).then(() => {
                        if (!this.cartLockedForProcessing) {
                            this.verifyOrderMode = true
                        }
                        else {
                            this.resetVerifyOrderMode()
                        }
                    }).catch(err => {
                        this.resetVerifyOrderMode()
                        throw err;
                    })
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: continueToVerifyOrder")
                    throw err;
                }
            },
            checkout() {
                try {
                    if (this.isStoreDisabled()) {
                        //If the store is disabled, but somehow the user is able to click the checkout button, make sure to exit here and do nothing.
                        return
                    }

                    if (!this.isLoggedIn) {
                        this.resetVerifyOrderMode()
                        this.showLogin = true
                        return
                    }

                    this.$store.dispatch('isCartOrderEditable', this.cart.orderID).then(() => {
                        if (!this.cartLockedForProcessing) {
                            if (this.checkoutSessionId) {
                                this.$refs.checkoutRef.redirectToCheckout()
                                this.loading = false
                                this.resetVerifyOrderMode()
                                return
                            }

                            this.loading = true

                            this.$store.dispatch('getPaymentSessionId', this.cart.orderID).then(() => {
                                if (this.getPaymentSessionIdData == 'ORDER_PROCESSED') {
                                    console.log('order processed')
                                    this.processPrepaidCart()
                                }
                                else if (this.getPaymentSessionIdData) {
                                    this.$store.commit('update_checkoutSessionId', this.getPaymentSessionIdData)
                                    //redirectToCheckout is where the customer is prompted for payment details and payment is taken.
                                    //Stripe will then redirect to the success or cancel URL set while creating the payment session and send any webhook events.
                                    this.$refs.checkoutRef.redirectToCheckout()
                                    this.loading = false
                                    this.resetVerifyOrderMode()
                                }
                            })
                        }
                        else {
                            this.resetVerifyOrderMode()
                        }

                    }).catch(err => {
                        this.resetVerifyOrderMode()
                        throw err;
                    })
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: checkout")
                    throw err;
                }
            },
            deleteItem(item) {
                try {
                    this.$store.dispatch('removeCartItem', item)
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: deleteItem")
                    throw err;
                }
            },
            updateQuantity(item) {
                try {
                    this.$store.dispatch('updateCartItemQuantity', item)
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: updateQuantity")
                    throw err;
                }
            },
            checkValidItems(item) {
                try {
                    this.$store.dispatch('validateCartItem', item)
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: checkValidItems")
                    throw err;
                }
            },
            onDiscountCodeChange(item) {
                try {
                    this.$store.commit('set_licenseOemLockedItemsViewed', false);
                    this.checkValidItems(item);
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: onDiscountCodeChange")
                    throw err;
                }
            },
            processPrepaidCart() {
                try {
                    this.$store.dispatch('processPrepaidCart', { cart: this.cart }).then(() => {
                        this.$router.push({
                            name: '/store/confirm', params: {
                                id: this.processCartData
                            }
                        }).catch(err => { })
                    })
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: processPrepaidCart")
                    throw err;
                }
            },
            resetVerifyOrderMode() {
                this.$store.commit('update_continueToVerifyOrderClicked', false)
                this.verifyOrderMode = false
            },
            closeLoginDialog(buttonClicked) {
                this.showLogin = false;

                if (buttonClicked != 'ForgotPassword' && buttonClicked != 'CreateNewAccount') {
                    this.$store.commit('update_continueToVerifyOrderClicked', false)
                }
            },
            unlockCart() {
                try {
                    const payload = {
                        orderId: this.cart.orderID,
                        checkoutSessionId: this.checkoutSessionId,
                    }
                    this.$store.dispatch('unlockCartForEditing', payload)
                } catch (err) {
                    this.addCustomErrorContext(err, "Method: unlockCart")
                    throw err;
                }
            },
            addCustomErrorContext(err, location) {
                if (typeof err !== 'object' || err === null) return;

                const contextKey = "\\src\\views\\Store\\Cart.Vue";

                if (!err._customContext) {
                    err._customContext = {};
                }

                if (!err._customContext[contextKey]) {
                    err._customContext[contextKey] = {
                        errorThrownCallStack: [],
                        data: {
                            licensesForRenewUpgrade: this.licensesForRenewUpgrade,
                            reinstatementLicenseItems: this.reinstatementLicenseItems,
                            verifyOrderMode: this.verifyOrderMode,
                        },
                        computed: {
                            cart: this.cart,
                            cartLockedForProcessing: this.cartLockedForProcessing,
                            checkoutSessionId: this.checkoutSessionId,
                            isCartValidForPurchase: this.isCartValidForPurchase,
                            oemLockedLicenseItems: this.oemLockedLicenseItems,
                        }
                    };
                }

                // Add the location to the array if it's not already present
                const locations = err._customContext[contextKey].errorThrownCallStack;
                if (!locations.includes(location)) {
                    locations.push(location);
                }

                return;
            },
        },
        computed: {
            ...mapState([
                'syncCartPending',
                'syncCartData',

                'getPaymentSessionIdPending',
                'getPaymentSessionIdData',

                'processCartPending',
                'processCartData',

                'isStoreDisabledPending',
                'isStoreDisabledData',
            ]),
            isLoggedIn() { return this.$store.getters.isLoggedIn },
            cart() { return this.$store.getters.cart },
            continueToVerifyOrderClicked() { return this.$store.getters.continueToVerifyOrderClicked },
            cartLockedForProcessing() { return this.$store.getters.cartLockedForProcessing },
            checkoutSessionId() { return this.$store.getters.checkoutSessionId },
            isCartValidForPurchase() { return this.$store.getters.isCartValidForPurchase },
            screenSize() {
                return this.$q.screen
            },
            showAskInstallID() {
                if (this.cartLockedForProcessing) return false
                if (this.$store.getters.installIdDismissed) return false
                return true
            },
            showLicenseOemLockedDialog() { return this.$store.getters.showLicenseOemLockedDialog },
            oemLockedLicenseItems() { return this.$store.getters.oemLockedLicenseItems },
            popupPosition() {
                return this.screenSize.lt.md
                    ? { anchor: 'bottom right', self: 'top right', arrow: 'arrow-top', contentClass: 'bg-transparent no-shadow q-pt-sm q-pl-lg' } // Below on smaller screens
                    : { anchor: 'top left', self: 'bottom left', arrow: 'arrow-bottom', contentClass: 'bg-transparent no-shadow q-pb-sm q-pr-lg' }; // Above on larger screens
            },
        },
        watch: {
            isLoggedIn(newVal, oldVal) {
                if (newVal) {
                    this.showLogin = false;
                    if (!oldVal && this.continueToVerifyOrderClicked) {
                        this.$store.commit('update_continueToVerifyOrderClicked', false)
                        this.verifyOrderMode = true
                        this.showLicenseOemLockedDialogOnContinue = this.showLicenseOemLockedDialog;
                    }
                }
                else {
                    this.resetVerifyOrderMode();
                }
            },
            showLicenseOemLockedDialog(newVal, oldVal) {
                if (!newVal) {
                    this.showLicenseOemLockedDialogOnContinue = false;
                }
            },
        },
        mounted() {
            window.scrollTo(0, 0);
            this.isStoreDisabled();
        },
        errorCaptured(err, vm, info) {
            this.addCustomErrorContext(err, "Hook: errorCaptured (Comes from Child Component)")
            return;
        },
    }
</script>
<style lang="scss" scoped>
    @import "../../styles/quasar.variables.scss";

    .img-flag {
        max-height: 35px;
    }

    .box {
        color: transparent;
        position: relative;
        border-radius: 4px;
    }

    .box.arrow-top {
        margin-top: 4px;
    }

    .box.arrow-top:after {
        content: " ";
        position: absolute;
        right: 0;
        top: -4px;
        border-top: none;
        border-right: 15px solid transparent;
        border-left: 15px solid transparent;
        border-bottom: 10px solid $secondary;
    }

    .box.arrow-bottom {
        margin-bottom: 4px;
    }

    .box.arrow-bottom:after {
        content: " ";
        position: absolute;
        left: 0;
        bottom: -4px;
        border-top: 10px solid $secondary;
        border-right: 15px solid transparent;
        border-left: 15px solid transparent;
        border-bottom: none;
    }

    .box.arrow-left {
        margin-left: 4px;
    }

    .box.arrow-left:after {
        content: " ";
        position: absolute;
        left: -4px;
        top: calc(50% - 15px);
        border-top: 15px solid transparent;
        border-right: 10px solid $secondary;
        border-left: none;
        border-bottom: 15px solid transparent;
    }


    .slide-fade-enter-active,
    .slide-fade-leave-active {
        transition: all 0.5s ease;
        overflow: hidden;
    }

    .slide-fade-enter,
    .slide-fade-leave-to {
        opacity: 0;
        height: 0;
        min-height: 0;
        margin-bottom: 0;
        padding-top: 0;
        padding-bottom: 0;
    }

    .fade-enter-active, .fade-leave-active {
        transition: opacity 0.3s ease;
    }

    .fade-enter, .fade-leave-to {
        opacity: 0;
    }

    .install-id-top-banner ::v-deep(.q-banner__content) {
        min-width: 219px;
    }

    .install-id-top-banner ::v-deep(.q-banner__actions) {
        margin-left: auto;
        margin-right: auto;
    }

    </style>
